/**
 * Deep-link prefill — turn URL query params into a starting QuoteRequest.
 *
 * Lets anything that can build a URL (a HubSpot workflow, a Zap, OptiClaw, a rep's
 * bookmark) hand a staff member a pre-filled quote:
 *
 *   /staff/optihub/quote-generator
 *     ?company=Northwind%20Community%20College&country=US&currency=usd
 *     &l=sub:proPlus:40&l=sub:aericast:12&l=hw:promax:4&l=hw:stick:36
 *
 * !!! THE LINK CAN NAME PRODUCTS AND QUANTITIES. IT CAN NEVER SET A PRICE. !!!
 *
 * That is the whole security property of this module, and it is why it exists as a
 * narrowing layer instead of a JSON passthrough. `QuoteRequestLine.unitPrice` overrides
 * the catalog by design (a rep typing a negotiated rate), so a parser that copied fields
 * through would let a crafted link quote anything at $0. Nothing here writes `unitPrice`,
 * and `linePrefillFields` pins that shape so a future edit can't quietly add it.
 *
 * Prices are resolved on load from productPlans / deviceProducts, so a tampered link
 * still cannot move money — the worst it can do is name a product that doesn't exist,
 * which the engine already reports as an unpriced, flagged line rather than guessing.
 *
 * Anything unrecognised is DROPPED and reported in `notices` rather than silently
 * ignored: a rep who was sent a link needs to know the link asked for something we
 * couldn't honour, or they'll send a quote missing a line the customer asked for.
 */
import { normalizeCountry } from './countries';
import { CURRENCIES } from './pricing/types';
import type {
  Currency,
  LineKind,
  QuoteCustomer,
  QuoteRequestLine,
  ShippingRegion,
} from './pricing/types';

export interface QuotePrefill {
  /** Absent when the link said nothing about it — the page keeps its own default. */
  currency?: Currency;
  customer: QuoteCustomer;
  lines: QuoteRequestLine[];
  /** Human-readable account of everything the link asked for that we did not honour. */
  notices: string[];
  /** True when the link carried anything at all — the page shows a banner. */
  applied: boolean;
}

/**
 * The ONLY line fields a link may populate. `unitPrice` is deliberately absent; see the
 * module comment. Typed as a tuple of keys so adding one is a visible, reviewable change.
 */
export const linePrefillFields = ['kind', 'sku', 'quantity', 'region', 'term'] as const;

const KIND_ALIASES: Record<string, LineKind> = {
  sub: 'subscription',
  subscription: 'subscription',
  plan: 'subscription',
  addon: 'addon',
  'add-on': 'addon',
  addons: 'addon',
  hw: 'device',
  hardware: 'device',
  device: 'device',
  ship: 'shipping',
  shipping: 'shipping',
};

const REGION_ALIASES: Record<string, ShippingRegion> = {
  us: 'internal',
  usa: 'internal',
  domestic: 'internal',
  internal: 'internal',
  intl: 'international',
  international: 'international',
};


let seq = 0;
function nextId(): string {
  seq += 1;
  return `link-${seq}`;
}

/** Trimmed value, or undefined when the param is absent or blank. */
function str(params: URLSearchParams, ...names: string[]): string | undefined {
  for (const n of names) {
    const v = params.get(n);
    if (v !== null && v.trim()) return v.trim();
  }
  return undefined;
}

/**
 * One `l=` value → a line. Format `kind:sku[:qty[:extra]]`, where `extra` is the term on a
 * subscription and the region on shipping. `sku` may not contain a colon (catalog ids are
 * hex or camelCase aliases, so this has not been a constraint in practice).
 */
function parseLine(raw: string, notices: string[]): QuoteRequestLine | null {
  const parts = raw.split(':').map((p) => p.trim());
  const [kindRaw, sku, qtyRaw, extra, ...rest] = parts;

  const kind = KIND_ALIASES[(kindRaw || '').toLowerCase()];
  if (!kind) {
    notices.push(
      `Ignored "${raw}" — unknown line type "${kindRaw}" (expected sub, addon, hw or ship).`,
    );
    return null;
  }
  if (!sku) {
    notices.push(`Ignored "${raw}" — no product given.`);
    return null;
  }

  // An unparseable quantity becomes null rather than a guess: the engine reports it as
  // "missing or invalid quantity" and blocks printing, which is what we want a rep to see.
  let quantity: number | null = null;
  if (qtyRaw !== undefined && qtyRaw !== '') {
    if (/^\d+$/.test(qtyRaw)) {
      quantity = Number(qtyRaw);
    } else {
      notices.push(`"${sku}": quantity "${qtyRaw}" is not a whole number — left blank for you to fill in.`);
    }
  } else {
    notices.push(`"${sku}": no quantity given — left blank for you to fill in.`);
  }

  const line: QuoteRequestLine = { id: nextId(), kind, sku, quantity };

  if (extra) {
    if (kind === 'shipping') {
      const region = REGION_ALIASES[extra.toLowerCase()];
      if (region) line.region = region;
      else notices.push(`"${sku}": ignored region "${extra}" (expected us or international).`);
    } else if (kind === 'subscription') {
      // Quotes are annual. A link asking for anything else is told so rather than being
      // quietly re-termed, because "I asked for monthly and got an annual figure" is a
      // conversation worth having before the quote goes out.
      const t = extra.toLowerCase();
      if (t !== 'yearly' && t !== 'annual') {
        notices.push(`"${sku}": ignored term "${extra}" — quotes are annual only.`);
      }
    } else if (kind === 'addon') {
      // An add-on's 4th value is a term OR a tier — `addon:backgroundmusic:10:pro` is the
      // common case. The engine validates the tier against the catalog and says so if it
      // does not exist.
      const t = extra.toLowerCase();
      if (t === 'yearly' || t === 'annual') {
        /* annual is the only term; nothing to record */
      } else if (t === 'monthly') {
        notices.push(`"${sku}": ignored term "${extra}" — quotes are annual only.`);
      } else {
        line.tier = extra;
      }
    } else {
      // A hardware line has no 4th field. This is the shape a price injection would take,
      // so say so out loud rather than dropping it quietly.
      notices.push(`"${sku}": ignored extra value "${extra}" — a link cannot set a price.`);
    }
  }
  if (rest.length) {
    notices.push(`"${sku}": ignored ${rest.length} extra value(s) — a link cannot set a price.`);
  }

  return line;
}

export function parseQuotePrefill(params: URLSearchParams): QuotePrefill {
  const notices: string[] = [];
  const lines: QuoteRequestLine[] = [];

  // Reject a price param outright and loudly, rather than ignoring it as unknown. If one
  // ever shows up in a real link, whoever built it needs to hear that it did nothing.
  for (const banned of ['unitprice', 'unit_price', 'price', 'amount', 'total']) {
    for (const key of params.keys()) {
      if (key.toLowerCase() === banned) {
        notices.push(`Ignored "${key}" — a link can name products and quantities, never prices.`);
      }
    }
  }

  // Discounts are a concession a rep decides on, not something a URL pre-loads. A link
  // that could set one could quote 100% off, which is the same hole as setting a price —
  // and unlike a quantity, nobody can eyeball a discount and tell it is wrong.
  for (const key of params.keys()) {
    if (['discount', 'discountpct', 'disc'].includes(key.toLowerCase())) {
      notices.push(`Ignored "${key}" — discounts are set per line on the form, not by a link.`);
    }
  }

  // Terms are legal text on a document the customer is asked to agree to. A link that
  // could rewrite them is strictly worse than one that could set a price: a wrong number
  // gets noticed at invoicing, a wrong term gets noticed in a dispute.
  for (const key of params.keys()) {
    if (['term', 'terms', 'bullet', 'bullets', 'tc'].includes(key.toLowerCase())) {
      notices.push(`Ignored "${key}" — terms & conditions are edited on the form, not by a link.`);
    }
  }

  for (const raw of [...params.getAll('l'), ...params.getAll('line')]) {
    if (!raw.trim()) continue;
    const line = parseLine(raw.trim(), notices);
    if (line) lines.push(line);
  }

  const customer: QuoteCustomer = {};
  const company = str(params, 'company');
  const name = str(params, 'name', 'contact');
  const email = str(params, 'email');
  const country = str(params, 'country');
  if (company) customer.company = company;
  if (name) customer.name = name;
  if (email) customer.email = email;
  if (country) customer.country = normalizeCountry(country);

  let currency: Currency | undefined;
  const curRaw = str(params, 'currency', 'cur');
  if (curRaw) {
    const c = curRaw.toLowerCase() as Currency;
    if ((CURRENCIES as readonly string[]).includes(c)) currency = c;
    else notices.push(`Ignored currency "${curRaw}" (expected ${CURRENCIES.join(', ')}).`);
  }


  const applied =
    lines.length > 0 ||
    Object.keys(customer).length > 0 ||
    currency !== undefined ||
    notices.length > 0;

  return { currency, customer, lines, notices, applied };
}
